import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { PublicProfile } from "@/components/profile/PublicProfileCard";

// Fields that are NEVER published to publicProfiles regardless of visibility
const PRIVATE_FIELDS = [
  "email",
  "password",
  "motDePasse",
  "address.street",
  "address.zipPostalCode",
  "phone",
  "subscriptionTier",
  "stripeCustomerId",
  "paymentStatus",
  "registrationData.password",
];

/**
 * Writes or updates the publicProfiles/{uid} document.
 * Called after profile save in useRoleProfile.
 * Respects the user's visibility settings.
 */
export async function publishProfile(
  uid: string,
  role: "broker" | "consultant" | "partner" | "buyer" | "seller",
  profileData: Record<string, any>,
  visibility: Record<string, boolean> = {}
): Promise<void> {
  const rd = profileData.registrationData || {};
  const spec0 = Array.isArray(rd.specializations)
    ? rd.specializations[0]
    : null;
  const addr = profileData.address || {};

  // Base public fields (all roles)
  const pub: Record<string, any> = {
    uid,
    role,
    updatedAt: serverTimestamp(),
    firstName: profileData.firstName || "",
    lastName: profileData.lastName || "",
    photoURL: profileData.photoURL || "",
    city: addr.city || rd.city || "",
    stateProvince: addr.stateProvince || rd.stateProvince || "",
    country: addr.country || rd.country || "",
    spokenLanguages: Array.isArray(rd.spokenLanguages)
      ? rd.spokenLanguages
      : [],
    status: profileData.status || "",
  };

  // Role-specific public fields
  if (role === "broker") {
    pub.firmName = profileData.firmName || "";
    pub.primarySector = spec0?.industry || "";
    pub.yearsExperience = spec0?.experienceYears || "";
    pub.feeStructure = Array.isArray(rd.feeStructure) ? rd.feeStructure : [];
    pub.bio = rd.professionalDescription || "";
    pub.motherTongue = rd.motherTongue || "";
    pub.licenseDetails = rd.licenses?.[0]?.details || "";
    pub.dealSizeMin = rd.dealSizeMin || "";
    pub.dealSizeMax = rd.dealSizeMax || "";
  }

  if (role === "consultant") {
    pub.companyName = profileData.companyName || "";
    pub.primarySector = spec0?.industry || "";
    pub.yearsExperience = spec0?.experienceYears || "";
    pub.bio = rd.services || rd.professionalDescription || "";
    pub.motherTongue = rd.motherTongue || "";
    pub.feeStructure = Array.isArray(rd.compensationOptions)
      ? rd.compensationOptions
      : [];
    pub.hourlyRate = rd.hourlyRate || "";
    pub.availability = rd.availability || "";
  }

  if (role === "partner") {
    pub.primarySector = spec0?.industry || "";
    pub.yearsExperience = spec0?.experienceYears || "";
    pub.feeStructure = Array.isArray(rd.feeStructure) ? rd.feeStructure : [];
    pub.addons = Array.isArray(profileData.partnerSubscription?.addons)
      ? profileData.partnerSubscription.addons
      : [];
    pub.motherTongue = rd.motherTongue || "";
    pub.licenseDetails = rd.licenses?.[0]?.details || "";
    pub.dealSizeMin = rd.dealSizeMin || "";
    pub.dealSizeMax = rd.dealSizeMax || "";
  }

  if (role === "buyer") {
    pub.selfDescription = profileData.selfDescription || "";
    pub.experience = profileData.experience || "";
    pub.subscriptionTier = profileData.subscriptionTier || "visitor";
    // Build readable budget range from sectors data
    const sectors = Array.isArray(profileData.secteurs)
      ? profileData.secteurs.map((s: any) => s.secteur).filter(Boolean)
      : [];
    pub.sectors = sectors;
    // Budget is private by default — only shown if user opts in via visibility
    const budgetMin = rd.budgetMin || "";
    const budgetMax = rd.budgetMax || "";
    pub.budgetRange = budgetMin && budgetMax
      ? `$${budgetMin} - $${budgetMax}`
      : "";
  }

  if (role === "seller") {
    pub.companyName = profileData.companyName || "";
    pub.industry = profileData.industry || "";
    pub.bio = profileData.reasonForSelling || "";
  }

  // Apply visibility overrides
  const visibilityOverrides: Record<string, string> = {
    firstName: "firstName",
    lastName: "lastName",
    "address.city": "city",
    "address.stateProvince": "stateProvince",
    "address.country": "country",
    firmName: "firmName",
    companyName: "companyName",
    selfDescription: "selfDescription",
    experience: "experience",
    "registrationData.professionalDescription": "bio",
    "registrationData.services": "bio",
    "registrationData.motherTongue": "motherTongue",
    "registrationData.spokenLanguages": "spokenLanguages",
    "registrationData.feeStructure": "feeStructure",
    "registrationData.dealSizeMin": "dealSizeMin",
    "registrationData.dealSizeMax": "dealSizeMax",
  };

  for (const [schemaKey, pubField] of Object.entries(visibilityOverrides)) {
    if (visibility[schemaKey] === false && pub[pubField] !== undefined) {
      pub[pubField] = Array.isArray(pub[pubField]) ? [] : "";
    }
  }

  // Budget range is private by default — only publish if explicitly allowed
  if (role === "buyer" && visibility["registrationData.budgetMin"] !== true) {
    pub.budgetRange = "";
  }

  await setDoc(doc(db, "publicProfiles", uid), pub, { merge: true });
}

/**
 * Removes a user from publicProfiles (on account deactivation or opt-out).
 */
export async function unpublishProfile(uid: string): Promise<void> {
  await deleteDoc(doc(db, "publicProfiles", uid));
}

/**
 * Loads public profiles by role from the publicProfiles collection.
 */
export async function loadPublicProfilesByRole(
  roles: Array<"broker" | "consultant" | "partner" | "buyer" | "seller">

): Promise<PublicProfile[]> {
  const results: PublicProfile[] = [];
  const seen = new Set<string>();

  for (const role of roles) {
    const snap = await getDocs(
      query(
        collection(db, "publicProfiles"),
        where("role", "==", role)
      )
    );
    for (const d of snap.docs) {
      if (seen.has(d.id)) continue;
      seen.add(d.id);
      const data = d.data();
      if (data.status === "pending_review" || data.status === "pending_payment") continue;
      results.push(data as PublicProfile);
    }
  }

  return results;
}

/**
 * Loads a single public profile by uid.
 */
export async function loadSinglePublicProfile(
  uid: string
): Promise<PublicProfile | null> {
  const snap = await getDoc(doc(db, "publicProfiles", uid));
  if (!snap.exists()) return null;
  return snap.data() as PublicProfile;
}
